import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const MeasurementManagement = () => {
  const [measurements, setMeasurements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    chest: '',
    waist: '',
    hips: '',
    length: '',
    sleeve: '',
    shoulder: '',
    neck: '',
    additional_measurements: ''
  });

  useEffect(() => {
    fetchMeasurements();
    fetchCustomers();
  }, []);

  const fetchMeasurements = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching measurements from:', API_ENDPOINTS.ADMIN.MEASUREMENTS);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.MEASUREMENTS, {
        timeout: 8000,
      });
      
      setMeasurements(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      handleApiError(error, 'fetch measurements');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await axios.get(API_ENDPOINTS.ADMIN.CUSTOMERS, {
        timeout: 8000,
      });
      
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Don't show error for customers fetch to avoid confusion
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleApiError = (error, operation) => {
    if (error.code === 'ECONNABORTED') {
      setError(`Request timeout while trying to ${operation}. Please check your connection.`);
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Failed to ${operation}.`);
    } else if (error.request) {
      setError(`Network error. Please check if the server is running.`);
    } else {
      setError(`Unable to ${operation}. Please try again later.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await axios.post(API_ENDPOINTS.ADMIN.MEASUREMENTS, formData, {
        timeout: 8000,
      });
      
      await fetchMeasurements();
      setShowForm(false);
      setFormData({
        customer_id: '', chest: '', waist: '', hips: '', 
        length: '', sleeve: '', shoulder: '', neck: '', additional_measurements: ''
      });
      alert('Measurement added successfully!');
    } catch (error) {
      console.error('Error creating measurement:', error);
      handleApiError(error, 'add measurement');
      alert('Error adding measurement. Please check the error message below.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this measurement?')) {
      try {
        setError('');
        await axios.delete(`${API_ENDPOINTS.ADMIN.MEASUREMENTS}/${id}`, {
          timeout: 8000,
        });
        
        await fetchMeasurements();
        alert('Measurement deleted successfully!');
      } catch (error) {
        console.error('Error deleting measurement:', error);
        
        if (error.response && error.response.status === 400) {
          alert('Cannot delete measurement used in existing orders. Please delete the orders first.');
        } else {
          handleApiError(error, 'delete measurement');
          alert('Error deleting measurement. Please check the error message below.');
        }
      }
    }
  };

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Measurement Management</h2>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(true)}
            disabled={customersLoading || customers.length === 0}
          >
            <i className="fas fa-plus me-2"></i>
            Add Measurement
          </button>
        </div>

        {/* Warning if no customers */}
        {customers.length === 0 && !customersLoading && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            No customers found. Please add customers first before creating measurements.
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {showForm && (
          <div className="card form-container mb-4">
            <div className="card-header">
              <h4 className="mb-0">Add New Measurement</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Customer *</label>
                      <select
                        className="form-control"
                        value={formData.customer_id}
                        onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                        required
                        disabled={loading || customersLoading}
                      >
                        <option value="">Select Customer</option>
                        {customers.map(customer => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name} {customer.phone ? `- ${customer.phone}` : ''}
                          </option>
                        ))}
                      </select>
                      {customersLoading && (
                        <div className="form-text">
                          <small>Loading customers...</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <h6 className="mb-3 text-muted">Body Measurements (in inches)</h6>
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Chest</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Chest measurement"
                        value={formData.chest}
                        onChange={(e) => setFormData({ ...formData, chest: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Waist</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Waist measurement"
                        value={formData.waist}
                        onChange={(e) => setFormData({ ...formData, waist: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Hips</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Hips measurement"
                        value={formData.hips}
                        onChange={(e) => setFormData({ ...formData, hips: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Length</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Length"
                        value={formData.length}
                        onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Sleeve</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Sleeve length"
                        value={formData.sleeve}
                        onChange={(e) => setFormData({ ...formData, sleeve: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Shoulder</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Shoulder width"
                        value={formData.shoulder}
                        onChange={(e) => setFormData({ ...formData, shoulder: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Neck</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        placeholder="Neck size"
                        value={formData.neck}
                        onChange={(e) => setFormData({ ...formData, neck: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Additional Measurements & Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Any additional measurements, special requirements, or notes..."
                    value={formData.additional_measurements}
                    onChange={(e) => setFormData({ ...formData, additional_measurements: e.target.value })}
                    disabled={loading}
                  ></textarea>
                </div>

                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Measurement'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Measurement Records</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading measurements...</p>
              </div>
            ) : measurements.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-ruler-combined fa-3x text-muted mb-3"></i>
                <p className="text-muted">No measurement records found.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowForm(true)}
                  disabled={customers.length === 0}
                >
                  Add First Measurement
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Customer</th>
                      <th>Chest</th>
                      <th>Waist</th>
                      <th>Hips</th>
                      <th>Length</th>
                      <th>Sleeve</th>
                      <th>Shoulder</th>
                      <th>Neck</th>
                      <th>Additional</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.map(measurement => (
                      <tr key={measurement.id}>
                        <td>{measurement.id}</td>
                        <td>
                          <strong>{measurement.customer_name}</strong>
                          {measurement.created_date && (
                            <div className="text-muted small">
                              Recorded: {new Date(measurement.created_date).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td>{measurement.chest ? `${measurement.chest}"` : '-'}</td>
                        <td>{measurement.waist ? `${measurement.waist}"` : '-'}</td>
                        <td>{measurement.hips ? `${measurement.hips}"` : '-'}</td>
                        <td>{measurement.length ? `${measurement.length}"` : '-'}</td>
                        <td>{measurement.sleeve ? `${measurement.sleeve}"` : '-'}</td>
                        <td>{measurement.shoulder ? `${measurement.shoulder}"` : '-'}</td>
                        <td>{measurement.neck ? `${measurement.neck}"` : '-'}</td>
                        <td>
                          {measurement.additional_measurements ? (
                            <span title={measurement.additional_measurements}>
                              {measurement.additional_measurements.length > 20 
                                ? `${measurement.additional_measurements.substring(0, 20)}...` 
                                : measurement.additional_measurements
                              }
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(measurement.id)}
                            disabled={loading}
                            title="Delete Measurement"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MeasurementManagement;